/**
 * Extracts and returns the store subdomain from the window hostname, if valid.
 * Returns undefined if it's not a store subdomain (e.g. www, api, v2, itdev, itdev-api, staging, prod-test, admin, distributor, funeralhome).
 */
export const getSubdomain = () => {
    const hostname = window.location.hostname;
    if (hostname.endsWith('infinitytouch.app')) {
        return undefined;
    }
    const parts = hostname.split('.');
    if (parts.length === 3 && !['www', 'api', 'v2', 'itdev', 'itdev-api', 'staging', 'prod-test', 'admin', 'distributor', 'funeralhome'].includes(parts[0])) {
        return parts[0];
    }
    return undefined;
};

export const isStoreSubdomain = () => {
    return getSubdomain() !== undefined;
};

/**
 * Builds a store-aware path.
 * On subdomain (cenosid.edisplayroom.com):  storePath('cenosid', '/shop') → '/shop'
 * On main domain (edisplayroom.com):        storePath('cenosid', '/shop') → '/store/cenosid/shop'
 *
 * Usage: storePath(subdomain, '/shop'), storePath(subdomain, `/product/${id}`)
 * Pass '' or '/' for the store root.
 */
export const storePath = (subdomain, path = '') => {
    const normalised = path.startsWith('/') ? path : `/${path}`;
    if (isStoreSubdomain()) {
        // On subdomain — paths are relative to root
        return normalised === '/' ? '/' : normalised;
    }
    // On main domain — prefix with /store/:subdomain
    const base = `/store/${subdomain}`;
    return normalised === '/' ? base : `${base}${normalised}`;
};

export const getStoreContext = (pathName = '') => {
    // Determine the current store context based on the URL path
    // Default to the window's hash or pathname if no explicit path is passed
    const path = pathName || window.location.pathname || '';

    // Look for /reseller in the path
    if (path.includes('/reseller')) {
        return 'funeral_store';
    }

    // Look for /store/:subdomain in the path
    const storeMatch = path.match(/\/store\/([^/?#]+)/);
    if (storeMatch && storeMatch[1]) {
        return storeMatch[1];
    }

    return 'main';
};

export const getStoreNamespacedKey = (baseKey, context = null) => {
    const activeContext = context || getStoreContext();
    return `${baseKey}_${activeContext}`;
};

/**
 * Generates a full URL for a store preview
 * Format: https://SUBDOMAIN.edisplayroom.com
 */
export const getStorePreviewUrl = (subdomain) => {
    if (!subdomain) return '#';
    
    // In production/staging, we use the subdomain format
    // If we're on localhost, we might still want to use the path format for local testing
    // or just use the target domain if that's what's requested.
    // Environments that use path-based routing (/store/subdomain) instead of subdomains
    const isPathBased = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname === 'v2.edisplayroom.com';
    
    if (isPathBased) {
        // Use path format for localhost/v2: https://v2.edisplayroom.com/store/subdomain
        return `${window.location.origin}/store/${subdomain}`;
    }
    
    // Use the primary domain structure for real production/staging
    const parts = window.location.hostname.split('.');
    const baseDomain = parts.slice(-2).join('.'); // Usually 'edisplayroom.com'
    
    return `https://${subdomain}.${baseDomain}`;
};
